import React, { useEffect, useState } from "react";
import { FaStar, FaStarHalfAlt } from "react-icons/fa";
import fetchLandingProducts from "../../common/libs/fetchLandingProducts";
import { Link, useSearchParams } from "react-router-dom";

// Static fake ratings (API se nahi aayegi)
const fakeRatings = [4.5, 4.3, 4.7, 4.2, 4.6, 4.4, 4.8, 4.3, 4.5, 4.6];

const ParkingTable = () => {
  const [products, setProducts] = useState([]);
  const [params] = useSearchParams();

  const getDeparture = params.get("departure");
  const getArrival = params.get("arrival");
  const getPromocode = params.get("promocode");
  const getAirport = params.get("airport");

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchLandingProducts("LGW", "");
        if (response?.success && Array.isArray(response.data)) {
          setProducts(response.data);
        }
      } catch (error) {
        console.error("productsUpdated error (Gatwick North Terminal)", error);
      }
    })();
  }, []);

  const renderStars = (rating) => {
    const value = parseFloat(rating) || 0;
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (value >= i) stars.push(<FaStar key={i} className="inline text-yellow" />);
      else if (value >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className="inline text-yellow" />);
      else stars.push(<FaStar key={i} className="inline text-gray-300" />);
    }
    return stars;
  };

  const formatPrice = (price) => {
    if (price) {
      const num = parseFloat(price);
      if (!Number.isNaN(num)) return `£${num.toFixed(2)}`;
    }
    return "N/A";
  };

  // Create URL-friendly slug from product name
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  // Filter products that serve North terminal only
  const getNorthTerminalProducts = () => {
    return products.filter((product) => {
      if (!Array.isArray(product?.terminals)) return false;
      return product.terminals.some((t) =>
        (t?.name || "").toLowerCase().includes("north")
      );
    });
  };

  const categorizeProducts = () => {
    const parkAndRide = [];
    const meetAndGreet = [];

    const northProducts = getNorthTerminalProducts();

    northProducts.forEach((product) => {
      const typeLabel = (product?.display_name || product?.type_name || "")
        .toString()
        .toLowerCase();

      if (typeLabel.includes("meet") && typeLabel.includes("greet")) {
        meetAndGreet.push(product);
      } else if (typeLabel.includes("park") && typeLabel.includes("ride")) {
        parkAndRide.push(product);
      }
    });

    return { parkAndRide, meetAndGreet };
  };

  const renderRows = (list) =>
    list.map((product, idx) => {
      const name = product?.name || "N/A";
      const image = product?.image?.src || product?.image_url || product?.image;
      const type = product?.display_name || product?.type_name || "N/A";
      const terminals = Array.isArray(product?.terminals)
        ? product.terminals.map((t) => t?.name).filter(Boolean).join(", ")
        : "N/A";
      const rating = fakeRatings[idx % fakeRatings.length]; // Static fake rating
      const companySlug = product?.slug || createSlug(name);

      return (
        <tr
          key={idx}
          className={`${
            idx % 2 === 0 ? "bg-[#e6f4f1]" : "bg-[#d2eff0]"
          } hover:bg-[#b9e5e7] transition`}
        >
          <td className="py-3 px-4 min-w-[250px]">
            <div className="flex items-center gap-3">
              {image && (
                <div className="flex items-center justify-center flex-shrink-0 w-16 h-16 p-1 bg-white rounded-lg shadow-sm">
                  <img
                    src={image}
                    alt={name}
                    className="object-contain w-full h-full"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <span className="font-semibold text-[#0c2d48]">{name}</span>
            </div>
          </td>
          <td className="px-4 py-3 whitespace-nowrap">{type}</td>
          <td className="px-4 py-3 font-medium whitespace-nowrap min-w-[250px]">{terminals}</td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-1">
              {renderStars(rating)}
              <span className="text-[#0c2d48] ml-1">({rating}/5)</span>
            </div>
          </td>
          <td className="px-4 py-3 text-center whitespace-nowrap min-w-[250px]">
            <Link
              to={`/gatwick-airport-parking/${companySlug}`}
              className="inline-block bg-[#00bcd4] hover:bg-[#009fb5] text-black font-semibold px-4 py-2 rounded-md transition"
            >
              Book Now
            </Link>
          </td>
        </tr>
      );
    });

  const { parkAndRide, meetAndGreet } = categorizeProducts();

  return (
    <div className="space-y-8 overflow-x-auto">
      {parkAndRide.length > 0 && (
        <div>
          <h3 className="mb-3 text-lg font-semibold text-[#0c2d48]">
            Park &amp; Ride Options
          </h3>
          <table className="w-full overflow-hidden text-sm border-collapse rounded-lg shadow-md sm:text-base">
            <thead>
              <tr className="bg-[#003366] text-white text-left">
                <th className="px-4 py-3">Parking Provider</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Terminal</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3 text-center">Book Now</th>
              </tr>
            </thead>
            <tbody>{renderRows(parkAndRide)}</tbody>
          </table>
        </div>
      )}

      {meetAndGreet.length > 0 && (
        <div>
          <h3 className="mb-3 text-lg font-semibold text-[#0c2d48]">
            Meet &amp; Greet Options
          </h3>
          <table className="w-full overflow-hidden text-sm border-collapse rounded-lg shadow-md sm:text-base">
            <thead>
              <tr className="bg-[#003366] text-white text-left">
                <th className="px-4 py-3">Parking Provider</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Terminal</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3 text-center">Book Now</th>
              </tr>
            </thead>
            <tbody>{renderRows(meetAndGreet)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ParkingTable;

